import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import type { InvoiceDiscountCode } from '@/pages/InvoiceDiscountCodeCard'
import type { Invoice, InvoiceResponse } from '@/pages/InvoicesPage.types'

interface InvoiceDiscountCodesResponse {
  discount_codes: InvoiceDiscountCode[]
}

export function useInvoiceDiscountCodes(invoiceId: number, invoice?: Invoice) {
  const queryClient = useQueryClient()
  const [selectedCode, setSelectedCode] = React.useState('')
  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false)
  const codesQuery = useQuery({
    queryKey: ['discount-codes'],
    queryFn: () => getJSON<InvoiceDiscountCodesResponse>('/api/v1/discount_codes?picker=true'),
    retry: false,
    enabled: invoice?.discount_code_change_available === true && !invoice.discount_row,
  })
  const apply = useMutation({
    mutationFn: () =>
      postJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}/discount_code`, {
        discount_code: { code: selectedCode },
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoiceId], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}/discount_code`),
    onSuccess: async (data) => {
      setSelectedCode('')
      setRemoveDialogOpen(false)
      queryClient.setQueryData(['invoice', invoiceId], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })

  return {
    codes: codesQuery.data?.discount_codes ?? [],
    loading: codesQuery.isLoading,
    failed: codesQuery.isError,
    selectedCode,
    setSelectedCode,
    pending: apply.isPending || remove.isPending,
    error: apply.error ?? remove.error,
    apply: () => apply.mutate(),
    remove: () => remove.mutate(),
    removePending: remove.isPending,
    removeFailed: remove.isError,
    removeDialogOpen,
    openRemoveDialog: () => setRemoveDialogOpen(true),
    closeRemoveDialog: () => setRemoveDialogOpen(false),
  }
}
